import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { CiteThis } from "@/components/site/CiteThis";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { SITE, formatDate } from "@/lib/site";

// NOTE: do not compute `new Date()` at module scope on Cloudflare Workers —
// it returns the epoch (1970) until the first request has been handled.


function serverPublicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

function withTimeout<T>(p: PromiseLike<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(fallback), ms);
    Promise.resolve(p).then(
      (v) => { clearTimeout(t); resolve(v); },
      () => { clearTimeout(t); resolve(fallback); },
    );
  });
}

/** Final publication timestamp — only meaningful once the year has ended. */
function reportPublishedAt(year: number): string {
  return `${year}-12-31T12:00:00.000Z`;
}


type VolRow = { snapshot_date: string; score: number; level: string };
type UpdateRow = { slug: string; name: string; severity: number; status: string; started_at: string; affected_surfaces: string[] | null };
type ArticleRow = { slug: string; title: string; published_at: string | null; category_id: string | null };
type CatRow = { id: string; slug: string; name: string };

export const Route = createFileRoute("/state-of-local-search/$year")({
  loader: async ({ params }) => {
    const reportYear = Number.parseInt(params.year, 10);
    // Static bounds — do not use `new Date()` here. On Cloudflare Workers Date is
    // frozen at the Unix epoch during prerender / cold start, so any upper bound
    // derived from the current year would 404 legitimate years (2026, 2027, …).
    if (!Number.isFinite(reportYear) || reportYear < 2020 || reportYear > 2100) {
      throw notFound();
    }
    const sb = serverPublicClient();
    const yearStart = `${reportYear}-01-01`;
    const yearEnd = `${reportYear + 1}-01-01`;

    const [volRes, upRes, artRes, catRes] = await Promise.all([
      withTimeout<{ data: VolRow[] | null }>(
        sb.from("volatility_snapshots")
          .select("snapshot_date, score, level")
          .gte("snapshot_date", yearStart)
          .lt("snapshot_date", yearEnd)
          .order("snapshot_date", { ascending: true })
          .limit(400) as unknown as PromiseLike<{ data: VolRow[] | null }>,
        5000,
        { data: null },
      ),
      withTimeout<{ data: UpdateRow[] | null }>(
        sb.from("algorithm_updates")
          .select("slug, name, severity, status, started_at, affected_surfaces")
          .eq("is_published", true)
          .gte("started_at", yearStart)
          .lt("started_at", yearEnd)
          .order("severity", { ascending: false })
          .limit(50) as unknown as PromiseLike<{ data: UpdateRow[] | null }>,
        5000,
        { data: null },
      ),
      withTimeout<{ data: ArticleRow[] | null }>(
        sb.from("articles")
          .select("slug, title, published_at, category_id")
          .eq("status", "published")
          .gte("published_at", yearStart)
          .lt("published_at", yearEnd)
          .order("published_at", { ascending: false })
          .limit(500) as unknown as PromiseLike<{ data: ArticleRow[] | null }>,
        5000,
        { data: null },
      ),
      withTimeout<{ data: CatRow[] | null }>(
        sb.from("categories").select("id, slug, name") as unknown as PromiseLike<{ data: CatRow[] | null }>,
        5000,
        { data: null },
      ),
    ]);

    // Request-time clock (never module scope on Workers).
    const now = new Date();
    const currentYear = now.getUTCFullYear();
    const isComplete = reportYear < currentYear;
    const volatility = volRes.data ?? [];
    const updates = upRes.data ?? [];
    const articles = artRes.data ?? [];

    // Newest real datapoint we hold for this year — used as the honest
    // "last updated" stamp for an in-progress edition.
    const stamps: string[] = [];
    if (volatility.length) stamps.push(`${volatility[volatility.length - 1].snapshot_date}T12:00:00.000Z`);
    if (articles.length && articles[0].published_at) stamps.push(articles[0].published_at);
    const lastUpdated = stamps.length ? stamps.sort().reverse()[0] : null;

    const hasData = volatility.length > 0 || articles.length > 0 || updates.length > 0;

    return {
      year: reportYear,
      isComplete,
      isFuture: reportYear > currentYear,
      hasData,
      lastUpdated,
      // Only a finished year gets a real publication date; an in-progress
      // edition is stamped with its most recent datapoint instead.
      datePublished: isComplete ? reportPublishedAt(reportYear) : (lastUpdated ?? now.toISOString()),
      dateModified: isComplete ? reportPublishedAt(reportYear) : (lastUpdated ?? now.toISOString()),
      volatility,
      updates,
      articles,
      categories: catRes.data ?? [],
    };
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE.url}/state-of-local-search/${params.year}`;
    const complete = loaderData?.isComplete ?? false;
    const hasData = loaderData?.hasData ?? false;
    const title = complete
      ? `State of Local Search ${params.year} — annual report | ${SITE.name}`
      : `State of Local Search ${params.year} — year-to-date report | ${SITE.name}`;
    const desc = complete
      ? `Map Observer's annual benchmark of Google Business Profile, the Map Pack, and AI search — built from a full year of volatility tracking, algorithm updates, and industry coverage.`
      : `Map Observer's running ${params.year} benchmark of Google Business Profile, the Map Pack, and AI search — updated as the year progresses, from live volatility tracking and algorithm coverage.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: `State of Local Search ${params.year}` },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        // An edition with no data yet is a placeholder — keep it out of the index.
        ...(hasData ? [] : [{ name: "robots", content: "noindex, follow" }]),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Report",
          name: `State of Local Search ${params.year}`,
          datePublished: loaderData?.datePublished,
          dateModified: loaderData?.dateModified,
          author: { "@type": "Organization", name: SITE.name, url: SITE.url },
          publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
          inLanguage: "en",
          about: ["Local SEO", "Google Business Profile", "Map Pack", "AI Overviews"],
          url,
        }),
      }],
    };
  },
  component: ReportPage,
});


function Sparkline({ data }: { data: VolRow[] }) {
  if (data.length < 2) return null;
  const W = 720;
  const H = 160;
  const PAD = 20;
  const maxScore = 10;
  const xs = (i: number) => PAD + (i * (W - 2 * PAD)) / (data.length - 1);
  const ys = (s: number) => H - PAD - (s / maxScore) * (H - 2 * PAD);
  const d = data.map((p, i) => `${i === 0 ? "M" : "L"}${xs(i).toFixed(1)},${ys(p.score).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto bg-paper-soft border border-border" role="img" aria-label="Weekly Map Pack volatility score">
      {[2, 4, 6, 8].map((g) => (
        <line key={g} x1={PAD} y1={ys(g)} x2={W - PAD} y2={ys(g)} stroke="currentColor" strokeOpacity="0.08" />
      ))}
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2" />
      {data.map((p, i) => (
        <circle key={p.snapshot_date} cx={xs(i)} cy={ys(p.score)} r="2.5" fill="currentColor" />
      ))}
      <text x={PAD} y={H - 4} fontSize="10" fill="currentColor" opacity="0.6">{data[0].snapshot_date}</text>
      <text x={W - PAD} y={H - 4} fontSize="10" fill="currentColor" opacity="0.6" textAnchor="end">{data[data.length - 1].snapshot_date}</text>
    </svg>
  );
}

function ReportPage() {
  const data = Route.useLoaderData() as {
    year: number;
    isComplete: boolean;
    isFuture: boolean;
    hasData: boolean;
    lastUpdated: string | null;
    volatility: VolRow[];
    updates: UpdateRow[];
    articles: ArticleRow[];
    categories: CatRow[];
  };
  const { year, isComplete, isFuture, hasData, lastUpdated, volatility, updates, articles, categories } = data;

  const avgScore = volatility.length
    ? volatility.reduce((a, v) => a + Number(v.score), 0) / volatility.length
    : 0;
  const peakScore = volatility.length ? Math.max(...volatility.map((v) => Number(v.score))) : 0;
  const peakDate = volatility.find((v) => Number(v.score) === peakScore)?.snapshot_date;
  const stormyWeeks = volatility.filter((v) => Number(v.score) >= 7).length;
  const confirmedUpdates = updates.filter((u) => u.status === "confirmed").length;
  const suspectedUpdates = updates.filter((u) => u.status === "suspected").length;

  const catMap = new Map(categories.map((c) => [c.id, c]));
  const catCounts = new Map<string, number>();
  for (const a of articles) {
    if (a.category_id) catCounts.set(a.category_id, (catCounts.get(a.category_id) ?? 0) + 1);
  }
  const topCats = [...catCounts.entries()]
    .map(([id, n]) => ({ cat: catMap.get(id), n }))
    .filter((x) => x.cat)
    .sort((a, b) => b.n - a.n)
    .slice(0, 6);

  if (!hasData) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main className="mx-auto max-w-3xl px-5 sm:px-8 py-20">
          <p className="eyebrow">{isFuture ? "Upcoming edition" : "Edition"} · {year}</p>
          <h1 className="headline-display text-4xl sm:text-6xl mt-3">State of Local Search {year}</h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            We have not published any tracked data for {year} yet. This page will fill in
            automatically as the Local Search Index, algorithm tracker, and newsroom record the
            year — and it becomes the final annual report once {year} closes.
          </p>
          <p className="mt-6 text-sm">
            <Link to="/local-search-index" className="text-accent underline">Follow the live index →</Link>
          </p>
          <NewsletterCTA />
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">{isComplete ? "Annual report" : "Year to date"} · {year}</p>
        <h1 className="headline-display text-4xl sm:text-6xl mt-3">State of Local Search {year}</h1>
        <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
          {isComplete ? (
            <>
              A year of Google Business Profile change, Map Pack volatility, and the slow
              encroachment of generative search on proximity-driven discovery — distilled from
              everything Map Observer tracked, covered, and measured in {year}.
            </>
          ) : (
            <>
              A running account of Google Business Profile change, Map Pack volatility, and the
              slow encroachment of generative search on proximity-driven discovery — covering
              1 January {year} to date, and updated as the year progresses.
            </>
          )}
        </p>
        <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">
          {isComplete
            ? `Published ${formatDate(reportPublishedAt(year))}`
            : `Year to date${lastUpdated ? ` · last updated ${formatDate(lastUpdated)}` : ""} · final report publishes 31 December ${year}`}
          {" "}· Free to cite with attribution
        </p>


        {/* TL;DR */}
        <aside className="mt-10 border-l-4 border-accent bg-paper-soft p-6 js-tldr" aria-label="TL;DR">
          <p className="eyebrow">TL;DR</p>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed">
            <li>→ Map Pack volatility averaged <strong>{avgScore.toFixed(1)}/10</strong> across {volatility.length} tracked weeks, peaking at <strong>{peakScore.toFixed(1)}</strong>{peakDate ? ` (${peakDate})` : ""}.</li>
            <li>→ <strong>{stormyWeeks}</strong> week{stormyWeeks === 1 ? "" : "s"} crossed into "stormy" territory (score ≥ 7).</li>
            <li>→ <strong>{confirmedUpdates}</strong> confirmed and <strong>{suspectedUpdates}</strong> suspected local-search updates tracked.</li>
            <li>→ Map Observer published <strong>{articles.length}</strong> articles across {topCats.length} active beat{topCats.length === 1 ? "" : "s"}.</li>
          </ul>
        </aside>

        {/* Section 1: Volatility */}
        <section className="mt-14">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">1. Map Pack volatility, week by week</h2>
          <p className="mt-4 text-sm leading-relaxed">
            The Local Search Index tracks Map Pack churn each week on a 0–10 scale. In {year},
            the index averaged <strong>{avgScore.toFixed(1)}</strong> with{" "}
            <strong>{stormyWeeks}</strong> stormy week{stormyWeeks === 1 ? "" : "s"}{" "}
            (score ≥ 7) — a useful benchmark for any agency or operator trying to separate
            client-specific issues from industry-wide movement.
          </p>
          <div className="mt-6">
            <Sparkline data={volatility} />
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span>Weekly score (0 = calm, 10 = turbulent)</span>
            <Link to="/local-search-index" className="text-accent underline">Live index →</Link>
          </div>
        </section>

        {/* Section 2: Updates */}
        <section className="mt-14">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">2. The {year} update timeline</h2>
          <p className="mt-4 text-sm leading-relaxed">
            Confirmed and suspected algorithm activity across the Map Pack, Local Finder, and
            related local-search surfaces in {year}, ranked by observed severity.
          </p>
          {updates.length === 0 ? (
            <p className="mt-6 text-sm text-muted-foreground">No tracked updates for this year yet — check back as the year progresses.</p>
          ) : (
            <ol className="mt-6 space-y-3">
              {updates.slice(0, 10).map((u) => (
                <li key={u.slug} className="border border-border p-4 flex items-start gap-4">
                  <div className="text-2xl font-bold w-12 text-accent">{Number(u.severity).toFixed(1)}</div>
                  <div className="flex-1">
                    <p className="font-semibold">
                      <Link to="/updates/$slug" params={{ slug: u.slug }} className="hover:text-accent">{u.name}</Link>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {u.status} · started {formatDate(u.started_at)} · {(u.affected_surfaces ?? []).join(", ") || "map-pack"}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Section 3: Editorial coverage */}
        <section className="mt-14">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">3. Where the story {isComplete ? "was" : "has been"} in {year}</h2>
          <p className="mt-4 text-sm leading-relaxed">
            Distribution of Map Observer&rsquo;s {articles.length} {year} stories{isComplete ? "" : " so far"} across
            our beats — a rough proxy for where the action {isComplete ? "was" : "has been"} in local search this year.
          </p>

          {topCats.length === 0 ? (
            <p className="mt-6 text-sm text-muted-foreground">No published stories in this window yet.</p>
          ) : (
            <ul className="mt-6 space-y-2">
              {topCats.map(({ cat, n }) => {
                const pct = articles.length ? (n / articles.length) * 100 : 0;
                return (
                  <li key={cat!.id}>
                    <div className="flex justify-between text-sm">
                      <Link to="/category/$slug" params={{ slug: cat!.slug }} className="hover:text-accent">{cat!.name}</Link>
                      <span className="text-muted-foreground">{n} · {pct.toFixed(0)}%</span>
                    </div>
                    <div className="mt-1 h-2 bg-paper-soft border border-border">
                      <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Methodology */}
        <section className="mt-14">
          <h2 className="headline-display text-2xl border-b border-foreground pb-2">Methodology</h2>
          <ul className="mt-4 list-disc pl-5 text-sm space-y-1">
            <li><strong>Volatility:</strong> weekly SERP samples across ~100 representative service-area queries, scored 0–10 against a rolling baseline.</li>
            <li><strong>Updates:</strong> a combination of official Google confirmations, our own measured movement, and corroborated practitioner reports.</li>
            <li><strong>Editorial coverage:</strong> every published Map Observer story between {year}-01-01 and {isComplete ? `${year}-12-31` : "today"}, classified by beat.</li>
            <li><strong>Reporting period:</strong> {isComplete ? `the complete ${year} calendar year.` : `1 January ${year} to date — figures on this page move as the year progresses, and are finalised on 31 December ${year}.`}</li>
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            Underlying data is available via the <a href="/api/v1" className="text-accent underline">public API</a>.
          </p>
        </section>

        <div className="mt-10 pt-6 border-t border-border">
          <CiteThis title={`State of Local Search ${year}${isComplete ? "" : " (year to date)"}`} url={`${SITE.url}/state-of-local-search/${year}`} author={`${SITE.name} Newsroom`} publishedDate={isComplete ? reportPublishedAt(year) : (lastUpdated ?? undefined)} />
        </div>


        <NewsletterCTA />
      </main>
      <SiteFooter />
    </div>
  );
}
